/**
 * CelebrationContext - Global celebration management
 *
 * Provides a central place to trigger celebrations from anywhere in the app:
 * - Level up celebrations
 * - Achievement unlocks
 * - Can be extended for other celebration types
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { LevelUpCelebration, AchievementUnlock } from '../components/celebrations';

const CelebrationContext = createContext();

export const useCelebration = () => {
  const context = useContext(CelebrationContext);
  if (!context) {
    throw new Error('useCelebration must be used within a CelebrationProvider');
  }
  return context;
};

export const CelebrationProvider = ({ children }) => {
  // Level up state
  const [levelUpData, setLevelUpData] = useState(null);

  // Achievement queue (can show multiple achievements)
  const [achievementQueue, setAchievementQueue] = useState([]);

  /**
   * Trigger a level up celebration
   * @param {number} newLevel - The new level reached
   */
  const celebrateLevelUp = useCallback((newLevel) => {
    setLevelUpData({ level: newLevel });
  }, []);

  /**
   * Close the level up celebration
   */
  const closeLevelUp = useCallback(() => {
    setLevelUpData(null);
  }, []);

  /**
   * Trigger an achievement unlock celebration
   * @param {object} achievement - Achievement data { name, description, icon, rarity, reward }
   */
  const celebrateAchievement = useCallback((achievement) => {
    const id = Date.now() + Math.random();
    setAchievementQueue((prev) => [...prev, { ...achievement, id }]);
  }, []);

  /**
   * Close the current achievement notification
   */
  const closeAchievement = useCallback((id) => {
    setAchievementQueue((prev) => prev.filter((a) => a.id !== id));
  }, []);

  /**
   * Celebrate multiple achievements at once (staggered)
   * @param {array} achievements - Array of achievement objects
   */
  const celebrateMultipleAchievements = useCallback((achievements) => {
    achievements.forEach((achievement, index) => {
      setTimeout(() => {
        celebrateAchievement(achievement);
      }, index * 1500); // Stagger by 1.5 seconds
    });
  }, [celebrateAchievement]);

  // Get the first achievement in the queue to display
  const currentAchievement = achievementQueue[0] || null;

  return (
    <CelebrationContext.Provider
      value={{
        celebrateLevelUp,
        closeLevelUp,
        celebrateAchievement,
        closeAchievement,
        celebrateMultipleAchievements,
        isLevelUpActive: !!levelUpData,
        hasActiveAchievement: !!currentAchievement,
      }}
    >
      {children}

      {/* Level Up Celebration */}
      <LevelUpCelebration
        isVisible={!!levelUpData}
        level={levelUpData?.level}
        onComplete={closeLevelUp}
      />

      {/* Achievement Unlock (shows one at a time from queue) */}
      <AchievementUnlock
        isVisible={!!currentAchievement}
        achievement={currentAchievement}
        onComplete={() => currentAchievement && closeAchievement(currentAchievement.id)}
        position="top-right"
      />
    </CelebrationContext.Provider>
  );
};

export default CelebrationContext;
